import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { isApiError, type CoreClientToServer, type CoreServerToClient } from '@tg/shared';
import { ApiRequestError, isKnownErrorCode } from '@/api/client';
import { useSession } from '@/stores/session';
import { wallet } from '@/wallet/wallet';
import { create } from 'zustand';

/**
 * One Socket.IO connection per tab on /socket.io (session cookie auth).
 * Reconnects every 3s (fixed, so the "reconnecting… 3s" banner can count down).
 * Games extend the event maps with their own contracts from @tg/shared/games/<game>:
 *   const s = getSocket<CrashServerToClient, CrashClientToServer>();
 */
export type AppSocket<S extends object = object, C extends object = object> = Socket<
  CoreServerToClient & S,
  CoreClientToServer & C
>;

export const RECONNECT_MS = 3000;

interface SocketStatus {
  retryAt: number | null;
}
export const useSocketStatus = create<SocketStatus>()(() => ({ retryAt: null }));

let socket: Socket | null = null;

export function getSocket<S extends object = object, C extends object = object>(): AppSocket<S, C> {
  if (!socket) {
    socket = io({
      path: '/socket.io',
      withCredentials: true,
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: RECONNECT_MS,
      reconnectionDelayMax: RECONNECT_MS,
      randomizationFactor: 0,
      transports: ['websocket', 'polling'],
    });
    attachCore(socket as AppSocket);
  }
  return socket as unknown as AppSocket<S, C>;
}

function attachCore(s: AppSocket): void {
  const session = useSession.getState;
  s.on('connect', () => {
    session().setConnection('online');
    useSocketStatus.setState({ retryAt: null });
  });
  s.on('disconnect', () => {
    session().setConnection('offline');
    useSocketStatus.setState({ retryAt: Date.now() + RECONNECT_MS });
  });
  s.on('connect_error', () => {
    session().setConnection('offline');
    useSocketStatus.setState({ retryAt: Date.now() + RECONNECT_MS });
  });
  s.io.on('reconnect_attempt', () => {
    useSocketStatus.setState({ retryAt: Date.now() + RECONNECT_MS });
  });
  s.on('wallet:balance', (e) => {
    void wallet.receive(e);
  });
}

export function connectSocket(): void {
  const s = getSocket();
  if (!s.connected) {
    useSession.getState().setConnection('connecting');
    s.connect();
  }
}

/** After sign in / sign out the cookie changed — reconnect so the server binds the new session. */
export function reconnectSocket(): void {
  const s = getSocket();
  s.disconnect();
  s.connect();
}

/** Try right now (the "Try again" button). */
export function retryNow(): void {
  const s = getSocket();
  if (!s.connected) s.connect();
}

/**
 * Emit with acknowledgement → Promise. Ack payload ApiError → rejects with ApiRequestError(code).
 * No ack within `timeoutMs` → rejects with NETWORK.
 */
export function emitAck<T>(event: string, ...args: unknown[]): Promise<T> {
  const s = getSocket() as unknown as Socket;
  return new Promise<T>((resolve, reject) => {
    s.timeout(8000).emit(event, ...args, (err: Error | null, r: unknown) => {
      if (err) return reject(new ApiRequestError('NETWORK', 0));
      if (isApiError(r)) {
        return reject(new ApiRequestError(isKnownErrorCode(r.error) ? r.error : 'INTERNAL', 0, r.params ?? {}));
      }
      resolve(r as T);
    });
  });
}

/** Subscribe to a socket event while mounted (handler may change between renders). */
export function useSocketEvent<A extends unknown[] = unknown[]>(event: string, handler: (...args: A) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const s = getSocket() as unknown as UntypedSocket;
    const fn = (...a: unknown[]) => ref.current(...(a as A));
    s.on(event, fn);
    return () => {
      s.off(event, fn);
    };
  }, [event]);
}

interface UntypedSocket {
  on(event: string, fn: (...a: unknown[]) => void): unknown;
  off(event: string, fn: (...a: unknown[]) => void): unknown;
}

/** Join a game room while mounted; re-joins after every reconnect. */
export function useRoom(game: string): void {
  useEffect(() => {
    const s = getSocket() as unknown as Socket;
    const join = () => s.emit('room:join', game, () => undefined);
    if (s.connected) join();
    s.on('connect', join);
    return () => {
      s.off('connect', join);
      if (s.connected) s.emit('room:leave', game);
    };
  }, [game]);
}

export const ACTIVITY_PING_MS = 60_000;

/**
 * Daily time limit heartbeat: `activity:ping` every 60s while a game screen is mounted and the tab is visible.
 * Used by GameShell — games get it for free.
 */
export function useActivityPing(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const s = getSocket() as unknown as Socket;
    const ping = () => {
      if (s.connected && document.visibilityState === 'visible' && useSession.getState().status === 'user') s.emit('activity:ping');
    };
    ping();
    const t = setInterval(ping, ACTIVITY_PING_MS);
    const onVis = () => document.visibilityState === 'visible' && ping();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled]);
}
